import { useEffect } from "react";
import { styled } from "styled-components";

import { SectionTitleUnderlined, UIButton } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { fetchTopicContentList } from "domains/customContentAndSources/actions";
import { ContentListItem } from "domains/customContentAndSources/components/ContentListItem";
import { createTopicCustomContentSelector } from "domains/customContentAndSources/selectors";
import { getCreateMapCustomContentAccess } from "entities/permissions/selectors";

const StyledContainer = styled.div`
  flex: 1;
  margin-bottom: 2rem;
`;

const StyledTopBar = styled.div`
  margin-bottom: 1rem;
`;

const StyledContentListItem = styled(ContentListItem)`
  padding: 1rem;
  margin-bottom: 1rem;
`;

type CreateCustomContentSectionProps = { onAddContentSelect?: () => void; topicId: string };

export const CreateCustomContentSection = ({ onAddContentSelect, topicId }: CreateCustomContentSectionProps) => {
  const dispatch = useDispatch();
  const canAddCustomContent = useSelector(getCreateMapCustomContentAccess);
  const translate = useTranslate();

  const content = useSelector(createTopicCustomContentSelector(topicId)) ?? [];

  useEffect(() => {
    if (topicId && content.length === 0) {
      dispatch(fetchTopicContentList({ topicId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, topicId]);

  if (!content.length && !canAddCustomContent) {
    return null;
  }

  const addContentButton = (
    <UIButton variant="neutral" onClick={onAddContentSelect}>
      {translate("app.custom_attachment.section.add.title")}
    </UIButton>
  );

  return (
    <StyledContainer>
      <StyledTopBar>
        <SectionTitleUnderlined rightElement={canAddCustomContent ? addContentButton : undefined}>
          {translate("app.custom_attachment.section.title")}
        </SectionTitleUnderlined>
      </StyledTopBar>
      {content.length === 0 ? (
        <UIButton onClick={onAddContentSelect} style={{ marginBottom: "1rem" }} variant="filled" fullwidth>
          {translate("app.custom_attachment.section.add.title")}
        </UIButton>
      ) : (
        content.map((item) => (
          <StyledContentListItem
            key={item.id}
            content={item}
            topicId={topicId}
            isEditing={canAddCustomContent}
            onEditSelect={onAddContentSelect}
          />
        ))
      )}
    </StyledContainer>
  );
};
